// Contract with BE admin-get-budget.ts; one policy for all admitted admin reads.
export const ADMIN_GET_TEXT_FIELD_BYTES = 512;
export const ADMIN_GET_TEXT_TOTAL_BYTES = 1024;
export const ADMIN_GET_REQUEST_LINE_BYTES = 4352;
type Rule =
  | 'text'
  | 'integer'
  | 'uuid'
  | 'optionalUuid'
  | 'date'
  | 'cursor'
  | readonly string[];
export const ADMIN_GET_RULES: Readonly<
  Record<string, Readonly<Record<string, Rule>>>
> = {
  delivery_templates: {
    offset: 'integer',
    limit: 'integer',
    query: 'text',
    sorting: 'text',
    order: ['ASC', 'DESC'],
    creation: 'text',
  },
  deletion_requests: {
    offset: 'integer',
    limit: 'integer',
    query: 'text',
    idToDelete: 'text',
    status: ['', '1', '2', '3'],
    type: ['', '1', '2'],
  },
  text_labels: { language: 'text' },
  tariff_plans: {
    offset: 'integer',
    limit: 'integer',
    query: 'text',
    sorting: ['default', 'creation_date', 'position'],
    order: ['ASC', 'DESC'],
    isActive: ['true', 'false'],
    isDefault: ['true', 'false'],
    isDeleted: ['true', 'false'],
    isPublic: ['true', 'false'],
  },
  'companies/brreg/search': {
    name: 'text',
  },
  'companies/email/search': {
    query: 'text',
  },
  companies: {
    query: 'text',
  },
  'companies/stats': {
    offset: 'integer',
    limit: 'integer',
    query: 'text',
    periodDays: ['1', '3', '7', '14', '30'],
    fromDate: 'date',
    toDate: 'date',
  },
  deliveries: {
    offset: 'integer',
    limit: 'integer',
    query: 'text',
    sorting: [
      'default',
      'creation_date',
      'end_client',
      'status',
      'title',
      'id',
    ],
    order: ['ASC', 'DESC'],
    type: ['', 'DEFAULT', 'USAGE_RIGHTS', 'APPROVED_OUTSIDE', 'PDF'],
    status: [
      '',
      'DRAFT',
      'AWAITING_APPROVAL',
      'APPROVED',
      'WITHDRAWN',
      'REPLACED',
      'REGISTERED',
      'REJECTED',
      'APPROVED_OUTSIDE',
      'SIGNATURE_REQUEST_EXPIRED',
    ],
    companyId: 'optionalUuid',
    approvedByCompanyId: 'optionalUuid',
  },
  'email_notifications/paginated': {
    offset: 'integer',
    limit: 'integer',
    query: 'text',
  },
  'fees/paginated': {
    offset: 'integer',
    limit: 'integer',
    query: 'text',
    sorting: ['default', 'price', 'quantity', 'order', 'tax'],
    order: ['ASC', 'DESC'],
  },
  'profiles/paginated': {
    offset: 'integer',
    limit: 'integer',
    query: 'text',
    sorting: ['default', 'creation_date', 'first_name', 'email', 'full_name'],
    order: ['ASC', 'DESC'],
  },
  'signup_requests/paginated': {
    offset: 'integer',
    limit: 'integer',
    query: 'text',
    sorting: ['default', 'creation_date', 'full_name', 'email', 'status'],
    order: ['ASC', 'DESC'],
    status: ['', 'AWAITING', 'APPROVED', 'REJECTED'],
  },
  'tags/paginated': {
    offset: 'integer',
    limit: 'integer',
    query: 'text',
    sorting: ['default', 'creation_date'],
    order: ['ASC', 'DESC'],
    type: ['', 'TAG', 'CATEGORY', 'OTHER'],
    labelType: ['', 'DP_ROLE', 'UR_AREA', 'UR_MC', 'COMPANY_ROLE'],
    label_type: ['', 'DP_ROLE', 'UR_AREA', 'UR_MC', 'COMPANY_ROLE'],
    creation: ['', 'CORIGHT', 'CUSTOM'],
    companyId: 'optionalUuid',
    categoryId: 'optionalUuid',
  },
  'terms/paginated': {
    offset: 'integer',
    limit: 'integer',
    query: 'text',
    sorting: ['default', 'type', 'end_client', 'title'],
    order: ['ASC', 'DESC'],
  },
  'usages/paginated': {
    offset: 'integer',
    limit: 'integer',
    query: 'text',
    sorting: [
      'default',
      'expiration_date',
      'end_client',
      'title',
      'creation_date',
    ],
    order: ['ASC', 'DESC'],
    status: [
      '',
      'ALL',
      'EXPIRED',
      'ACTIVE',
      'ABOUT_TO_EXPIRE',
      'UNKNOWN',
      'NOT_ACTIVE_YET',
    ],
  },
  'authorization-decisions': {
    fromDate: 'date',
    toDate: 'date',
    subjectProfileId: 'uuid',
    subjectUserId: 'uuid',
    actingCompanyId: 'uuid',
    entityId: 'uuid',
    entityType: 'text',
    action: 'text',
    reason: 'text',
    platformRole: 'text',
    requestId: 'text',
    allowed: ['true', 'false'],
    limit: 'integer',
    cursor: 'cursor',
  },
};
const UUID_FORMAT = new RegExp(
  '^(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}' +
    '|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$',
  'i',
);
export const utf8Bytes = (value: string): number => {
  try {
    return encodeURIComponent(value).replace(/%[0-9a-f]{2}/gi, '_').length;
  } catch {
    return Infinity;
  }
};
export const ADMIN_GET_LENGTH_ERROR =
  'Filter is too long or invalid. Text must be at most 512 UTF-8 bytes per field and 1024 bytes in total.';
export type AdminGetRequest = {
  url: string;
  method?: string;
  params?: Record<string, unknown>;
};
export function boundAdminGetRequest<T extends AdminGetRequest>(
  args: T,
): { request?: T; error?: string } {
  if ((args.method ?? 'GET').toUpperCase() !== 'GET') return { request: args };
  let path: string;
  try {
    path = new URL(args.url, 'https://admin.invalid/').pathname.replace(
      /^\//,
      '',
    );
  } catch {
    return { request: args };
  }
  const rules = ADMIN_GET_RULES[path.replace(/^admin\//, '')];
  if (!path.startsWith('admin/') || !rules) return { request: args };
  if (
    args.url.includes('?') ||
    (args.params !== undefined &&
      (!args.params ||
        typeof args.params !== 'object' ||
        Array.isArray(args.params)))
  )
    return { error: ADMIN_GET_LENGTH_ERROR };
  const params: Record<string, string | number> = {};
  let total = 0;
  for (const [name, rule] of Object.entries(rules)) {
    const value = args.params?.[name];
    if (value === undefined) continue;
    if (typeof value !== 'string' && typeof value !== 'number')
      return { error: ADMIN_GET_LENGTH_ERROR };
    const scalar = String(value);
    if (rule === 'text') {
      if (
        typeof value !== 'string' ||
        utf8Bytes(value) > ADMIN_GET_TEXT_FIELD_BYTES ||
        (path === 'admin/authorization-decisions' &&
          Array.from(value).length > 200)
      )
        return { error: ADMIN_GET_LENGTH_ERROR };
      total += utf8Bytes(value);
    } else if (rule === 'integer') {
      if (!/^\d{1,16}$/.test(scalar) || !Number.isSafeInteger(Number(scalar)))
        return { error: ADMIN_GET_LENGTH_ERROR };
    } else if (rule === 'uuid' || rule === 'optionalUuid') {
      if (
        !(rule === 'optionalUuid' && scalar === '') &&
        !UUID_FORMAT.test(scalar)
      )
        return { error: ADMIN_GET_LENGTH_ERROR };
    } else if (rule === 'date') {
      if (
        scalar.length > 40 ||
        !/(?:Z|[+-]\d{2}:\d{2})$/.test(scalar) ||
        !Number.isFinite(Date.parse(scalar))
      )
        return { error: ADMIN_GET_LENGTH_ERROR };
    } else if (rule === 'cursor') {
      if (!/^[A-Za-z0-9_-]{1,200}$/.test(scalar))
        return { error: ADMIN_GET_LENGTH_ERROR };
    } else if (!rule.includes(scalar)) return { error: ADMIN_GET_LENGTH_ERROR };
    params[name] = value;
  }
  if (total > ADMIN_GET_TEXT_TOTAL_BYTES)
    return { error: ADMIN_GET_LENGTH_ERROR };
  const query = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).map(([name, value]) => [name, String(value)]),
    ),
  ).toString();
  if (
    utf8Bytes('GET /' + path + (query ? '?' + query : '') + ' HTTP/1.1\r\n') >
    ADMIN_GET_REQUEST_LINE_BYTES
  )
    return { error: ADMIN_GET_LENGTH_ERROR };
  return { request: { ...args, params } };
}
